import { Leaf } from "lucide-react"
import React from "react"
import { Link } from "react-router-dom"
import { Card } from "randmarcomps"
import type { OpportunityEntry } from "randmar-api-client"
import { useManufacturer } from "../../contexts/WithManufacturer"
import Path from "../../routes/Path"
import type { ManufacturerBid } from "../../stores/search/SearchResult"
import { FormatDateFromString } from "../util/DateTimeUtil"

interface IProps {
  opportunities: ManufacturerBid | OpportunityEntry
}

const isLegacyOpportunity = (
  opportunity: ManufacturerBid | OpportunityEntry
): opportunity is ManufacturerBid => {
  // Legacy ManufacturerBid type from MobX store has camelCase properties
  return "bidNumber" in opportunity
}

const OpportunityCard: React.FC<IProps> = ({ opportunities }) => {
  const { applicationId } = useManufacturer()

  const { active, bidNumber, opportunity, endDate } = React.useMemo(() => {
    if (isLegacyOpportunity(opportunities)) {
      return {
        active: opportunities.active,
        bidNumber: opportunities.bidNumber,
        opportunity: opportunities.opportunity,
        endDate: opportunities.endDate,
      }
    }
    return {
      active: opportunities.Active,
      bidNumber: opportunities.BidNumber,
      opportunity: opportunities.Opportunity,
      endDate: opportunities.EndDate,
    }
  }, [opportunities])

  const statusStyles = active
    ? {
        iconContainer: "bg-green-100 text-green-600",
        badge: "bg-green-100 text-green-800",
        text: "Active",
      }
    : {
        iconContainer: "bg-red-100 text-red-600",
        badge: "bg-red-100 text-red-800",
        text: "Inactive",
      }

  return (
    <Card className="transition-shadow bg-white border rounded-lg shadow-sm group hover:shadow-md">
      <Link
        to={Path.opportunity(applicationId, bidNumber ?? "")}
        className="block p-4"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${statusStyles.iconContainer}`}
            >
              <Leaf className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-primary">{opportunity}</h3>
              <p className="mt-1 font-mono text-sm text-gray-500">{bidNumber}</p>
            </div>
          </div>
          <div
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold shrink-0 ${statusStyles.badge}`}
          >
            {statusStyles.text}
          </div>
        </div>
        {endDate && (
          <div className="pt-3 mt-3 text-xs text-gray-500 border-t">
            <span className="font-medium text-gray-600">End Date:</span> {FormatDateFromString(endDate)}
          </div>
        )}
      </Link>
    </Card>
  )
}

export default OpportunityCard
